"use client";

import SignupFormFooter from "@/components/authen/signup/SignupFormFooter";
import FormItem from "@/components/common/Form/Item";
import InputPassword from "@/components/common/Input/Password";
import Link from "@/components/common/Typography/Link";
import Paragraph from "@/components/common/Typography/Paragraph";
import Text from "@/components/common/Typography/Text";
import Title from "@/components/common/Typography/Title";
import FacebookFillIcon from "@/components/icons/FacebookFillIcon";
import { useAppDispatch } from "@/lib/redux/hooks";
import { thunks } from "@/lib/redux/thunks";
import { Button, Checkbox, Col, Divider, Form, Input, Row, message } from "antd";
import { toast } from "react-toastify";
import Image from "next/image";
import { useState } from "react";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { useForm } from "react-hook-form";
import ControllerWarpper from "@/components/common/ControllerWarpper";
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { validatePhoneNumber } from "@/utils/validates/validatePhoneNumber";
import { formatPhoneNumber } from "@/utils/app/formatPhoneNumber";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useTranslation } from "react-i18next";

type SignupFormProps = {
  onSwitchToLogin?: () => void;
  handleClose?: () => void;
};

const schema = yup.object({
  email: yup.string().required("auth.username_required_message"),
  password: yup.string().required("auth.password_required_message")
    .test(
      "password",
      "auth.password_rule_message",
      function (val) {
        return val?.length >= 8
      }
    ),
  confirmPassword: yup.string().required("auth.confirm_password_required_message")
    .test(
      "confirmPassword",
      "auth.confirm_password_nonematch_message",
      function (val) {
        return val === this.parent.password
      }
    ),
  hasReferal: yup.boolean().optional().nullable(),
  referralPhoneNumber: yup.string().optional().nullable()
    .test(
      "referralPhoneNumber",
      "auth.referral_code_format_message",
      function (val) {
        return !val || validatePhoneNumber(val)
      }
    ),
}).required()

const SignupForm = ({ onSwitchToLogin, handleClose }: SignupFormProps) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const router = useTERouter();
  const isDesktop = useIsDesktop();
  const form = useForm({
    resolver: yupResolver(schema)
  })
  const { t } = useTranslation()

  const onFinish = async (values: {
    email: string;
    password: string;
    confirmPassword: string;
    hasReferal?: boolean | null;
    referralPhoneNumber?: string | null
  }) => {

    setLoading(true);
    try {
      const response = await dispatch(
        thunks.regist({
          body: {
            TYPE: "JSON",
            email: values.email,
            password: values.password,
            confirmPassword: values.confirmPassword,
            referralPhoneNumber: values?.hasReferal ? formatPhoneNumber(values.referralPhoneNumber || "") : null
          },
        })
      );

      if (!response.payload) {
        toast.error(t("auth.signup_failed"));
      } else {
        if (response.payload.isNew === false) {
          toast.success(
            t("auth.username_be_used_message")
          );
        } else {
          toast.success(t("auth.signup_succeed"));
        }
        form.reset({
          email: "",
          password: "",
          confirmPassword: "",
          hasReferal: null,
          referralPhoneNumber: null,
        })

        // On mobile devices, redirect to login page after successful signup
        if (!isDesktop && response.payload) {
          if (onSwitchToLogin) {
            onSwitchToLogin();
          } else {
            // If there's no onSwitchToLogin function (for external form), navigate to login page
            router.push("/login");
          }
          return;
        }
      }

      if (handleClose) {
        handleClose();
      }
    } catch (error: any) {
      toast.error(error?.message || t("auth.signup_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const googleOAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}&redirect_uri=${window.location.origin}/auth/google/callback&response_type=code&scope=email profile`;
      window.location.href = googleOAuthUrl;
    } catch (error) {
      message.error(t("auth.signin_gg_failed"));
    }
  };

  const handleFacebookSignup = async () => {
    try {
      const facebookUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}&redirect_uri=${window.location.origin}/auth/facebook/callback&response_type=code&scope=email`;
      window.location.href = facebookUrl;
    } catch (error) {
      console.error("Error:", error);
      message.error(t("auth.signin_fb_failed"));
    }
  };

  return (
    <Form
      layout="vertical"
      className="form-login"
    // form={form}
    // onFinish={onFinish}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit(onFinish)();
        }}
      >
        <Title level={2} style={{ textAlign: "center", marginTop: 0 }}>
          {t("common.register")}
        </Title>
        <Paragraph style={{ textAlign: "center" }}>
          {t("auth.login_message")}
        </Paragraph>
        <ControllerWarpper
          name="email"
          control={form.control}
          render={({ field }) => {
            return (
              <Input
                {...field}
                type="text"
                size="large"
                placeholder={t("auth.username_placeholder")}
              />
            )
          }}
        />
        <ControllerWarpper
          name="password"
          control={form.control}
          // rules={[
          //   { required: true, message: "Please enter your password!" },
          //   { min: 8, message: "Password must be at least 8 characters!" },
          // ]}
          render={({ field }) => {
            return (
              <InputPassword {...field} size="large" placeholder={t("auth.password_placeholder")} />
            )
          }}
        />
        <ControllerWarpper
          control={form.control}
          name="confirmPassword"
          // rules={[{ required: true, message: "Please confirm your password!" }]}
          render={({ field }) => {
            return (
              <InputPassword {...field} size="large" placeholder={t("auth.confirm_password_placeholder")} />
            )
          }}
        />

        <ControllerWarpper
          control={form.control}
          name="hasReferal"
          render={({ field }) => {
            return (
              <>
                <Checkbox checked={!!field.value} onChange={() => field.onChange(!field.value)}>
                  {t("auth.have_a_referral_code")}
                </Checkbox>
                {field.value && (
                  <ControllerWarpper
                    style={{ marginTop: "0.8rem" }}
                    name={"referralPhoneNumber"}
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <Input {...field} value={field.value ?? undefined} placeholder={t("auth.referral_code_placeholder")} />
                      )
                    }}
                  />
                )}
              </>
            )
          }}
        />

        <FormItem>
          <Button
            onClick={(e) => {
              e.preventDefault()
              form.handleSubmit(onFinish)()
            }}
            size="large"
            type="primary"
            htmlType="submit"
            block
            loading={loading}
          >
            {t("common.create_account")}
          </Button>
        </FormItem>

        <Row className="form-login--divider-box">
          <Divider className="form-login--divider te-text-small">
            {t("auth.other_options_label_regist")}
          </Divider>
        </Row>
        <Row justify="center" className="form-login--socialmedia">
          <Col className="form-login--socialmedia-item">
            <Button
              onClick={handleFacebookSignup}
              shape="circle"
              size="large"
              icon={<FacebookFillIcon />}
            />
          </Col>
          <Col className="form-login--socialmedia-item">
            <Button
              onClick={handleGoogleSignup}
              shape="circle"
              size="large"
              icon={
                <Image
                  alt="Google logo"
                  src={"/static/google-logo.png"}
                  height={48}
                  width={48}
                />
              }
            />
          </Col>
        </Row>
        <Row justify="center" className="form-login--policy">
          <Text>
            {t("auth.term_and_condition_1")}{" "}
            <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">{t("home.policy_regulation")}</Link> {t("common.and")}{" "}
            <Link href="/chinh-sach-bao-mat" target="_blank" rel="noopener noreferrer">{t("home.privacy_policy")}</Link> {t("auth.term_and_condition_2", { platform: process.env.NEXT_PUBLIC_PLATFORM_NAME || "" })}.
          </Text>
        </Row>
        <SignupFormFooter onSwitchToLogin={onSwitchToLogin} />
      </form>
    </Form>
  );
};

export default SignupForm;
